import React from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, Clock, FolderOpen, Play } from 'lucide-react'

/** The connection points edges attach to. They are invisible on purpose — the
 *  plan is a fixed chain, users never draw their own connections, so a visible
 *  port would only invite a gesture that does nothing. */
const HANDLE_CLASS = 'opacity-0'

export type StepNodeData = {
  index: number
  name: string
  durationDays: number | null
  reviewerInitials: string[]
  reviewerOverflow: number
  reviewerCount: number
  invalid: boolean
  isSelected: boolean
}

export type PublishNodeData = {
  folderName: string | null
  selectable: boolean
  isSelected: boolean
}

export const StartNode: React.FC<NodeProps> = () => {
  const { t } = useTranslation()

  return (
    <>
      <div className="flex h-9 w-[104px] items-center justify-center gap-1.5 rounded-full border bg-muted px-3 text-sm font-medium text-muted-foreground">
        <Play className="h-3.5 w-3.5" />
        {t('Submittals_wf_flow_start')}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} className={HANDLE_CLASS} />
    </>
  )
}

export const StepNode: React.FC<NodeProps> = ({ data }) => {
  const { t } = useTranslation()
  const step = data as unknown as StepNodeData

  return (
    <>
      <Handle type="target" position={Position.Left} isConnectable={false} className={HANDLE_CLASS} />

      <div
        className={cn(
          'flex h-[104px] w-56 cursor-pointer flex-col justify-between rounded-lg border bg-card p-2.5 shadow-sm transition-colors hover:border-primary/60',
          step.isSelected && 'border-primary ring-2 ring-primary/25',
          step.invalid && 'border-destructive ring-2 ring-destructive/25',
        )}
      >
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="h-5 shrink-0 px-1.5">
            {step.index + 1}
          </Badge>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm font-medium',
              !step.name.trim() && 'font-normal italic text-muted-foreground',
            )}
          >
            {step.name.trim() || t('Submittals_wf_step_name_placeholder')}
          </span>
          {step.invalid && <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          {step.durationDays
            ? `${step.durationDays} ${t('Submittals_wf_step_days')}`
            : t('Submittals_wf_flow_no_duration')}
        </div>

        <div className="flex h-5 items-center gap-1">
          {step.reviewerCount === 0 ? (
            // Red only when unstaffed is actually an error — in template mode
            // (invalid=false) a skeleton step is fine and reads muted.
            <span className={step.invalid ? 'text-xs font-medium text-destructive' : 'text-xs font-medium text-muted-foreground'}>
              {t('Submittals_wf_flow_no_reviewers_short')}
            </span>
          ) : (
            <>
              {step.reviewerInitials.map((initials, i) => (
                <span
                  key={`${initials}-${i}`}
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
                >
                  {initials}
                </span>
              ))}
              {step.reviewerOverflow > 0 && (
                <span className="text-[10px] font-semibold text-muted-foreground">
                  +{step.reviewerOverflow}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} isConnectable={false} className={HANDLE_CLASS} />
    </>
  )
}

export const PublishNode: React.FC<NodeProps> = ({ data }) => {
  const { t } = useTranslation()
  const publish = data as unknown as PublishNodeData

  return (
    <>
      <Handle type="target" position={Position.Left} isConnectable={false} className={HANDLE_CLASS} />

      <div
        className={cn(
          'flex h-[72px] w-44 flex-col justify-center gap-1 rounded-lg border bg-card p-2.5 shadow-sm transition-colors',
          publish.selectable && 'cursor-pointer hover:border-primary/60',
          publish.isSelected && 'border-primary ring-2 ring-primary/25',
        )}
      >
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
          {t('Submittals_wf_flow_publish')}
        </div>
        <span className="truncate text-xs text-muted-foreground">
          {publish.folderName || t('Submittals_wf_flow_no_folder')}
        </span>
      </div>
    </>
  )
}

/** Defined once at module scope — a fresh object on every render makes React
 *  Flow remount every node (see its "nodeTypes changed" warning). */
export const EDITOR_NODE_TYPES = {
  start: StartNode,
  step: StepNode,
  publish: PublishNode,
}
